import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, ClipboardList, Wallet, TrendingUp, Radio, ListChecks, Smile, Timer, FileSearch, MessagesSquare, FolderCheck, Globe2, FileEdit, BellRing, Lightbulb, FileBadge, UserCheck } from "lucide-react";
import { MaskedLine, FadeUp, Stagger, StaggerItem, Magnetic } from "@/components/motion-primitives";
import { useConsult } from "@/components/ConsultProvider";
import { COUNTRIES, CONTACT } from "@/data/content";

const FEATURES = [
  { icon: ClipboardList, title: "Personalized Visa Plans" },
  { icon: Wallet, title: "Financial Document Support" },
  { icon: TrendingUp, title: "High Approval Rate" },
  { icon: Radio, title: "Real-Time Assistance" },
  { icon: ListChecks, title: "Step-by-Step Help" },
  { icon: Smile, title: "Student-Friendly Process" },
  { icon: Timer, title: "Quick Turnaround" },
  { icon: FileSearch, title: "Reliable Documentation Review" },
];

const STEPS = [
  { icon: MessagesSquare, title: "Mock Interview Training", desc: "Realistic embassy-style practice sessions that build genuine confidence." },
  { icon: FolderCheck, title: "Document Checklist Help", desc: "Every paper organized, reviewed and attested before submission." },
  { icon: Globe2, title: "Country-Specific Guidance", desc: "Fully updated on the latest visa rules for each destination." },
  { icon: FileEdit, title: "Application Form Review", desc: "Forms checked line by line for complete, accurate information." },
  { icon: BellRing, title: "Timely Updates", desc: "You hear from us at every stage — never left wondering." },
  { icon: Lightbulb, title: "Visa Success Tips", desc: "Insider strategies and real interview techniques that work." },
  { icon: FileBadge, title: "Error-Free Submissions", desc: "Precision filing that avoids the mistakes which cause refusals." },
  { icon: UserCheck, title: "One-on-One Support", desc: "A dedicated visa expert walks with you from start to stamp." },
];

export default function VisaGuidance() {
  const { openConsult } = useConsult();
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} data-testid="visa-page">
      <section className="relative overflow-hidden bg-navy pb-24 pt-40 text-white" data-testid="visa-hero">
        <div className="noise-overlay absolute inset-0" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6">
          <MaskedLine delay={0.9}>
            <span className="font-accent text-xs font-semibold uppercase tracking-[0.3em] text-gold">Visa Guidance</span>
          </MaskedLine>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
            <MaskedLine delay={1.0}>A visa is not just a document —</MaskedLine>
            <MaskedLine delay={1.12}><span className="italic text-gold">it's your gateway.</span></MaskedLine>
          </h1>
          <MaskedLine delay={1.3}>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
              Expert assistance to ensure your study abroad journey takes off — proven strategies, personalized support, and a high success rate.
            </p>
          </MaskedLine>
        </div>
      </section>

      <section className="border-y border-line bg-white py-24 md:py-28" data-testid="visa-features">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Why Students Trust Us</p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
              Every angle of the process, <span className="italic text-gold">covered</span>
            </h2>
          </FadeUp>
          <Stagger className="mt-14 grid grid-cols-2 gap-5 md:grid-cols-4" gap={0.06}>
            {FEATURES.map((f, i) => (
              <StaggerItem key={f.title}>
                <div data-testid={`visa-feature-${i}`} className="group flex h-full flex-col items-center rounded-xl border border-line bg-surface p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:bg-white hover:shadow-lift">
                  <f.icon className="h-8 w-8 text-gold" strokeWidth={1.5} />
                  <p className="mt-4 font-heading text-sm font-bold text-navy">{f.title}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="visa-steps">
        <FadeUp>
          <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">How It Works</p>
          <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Eight steps to your <span className="italic text-gold">visa stamp</span>
          </h2>
        </FadeUp>
        <Stagger className="mt-16 grid grid-cols-1 gap-x-12 gap-y-12 md:grid-cols-2" gap={0.08}>
          {STEPS.map((s, i) => (
            <StaggerItem key={s.title}>
              <div data-testid={`visa-step-${i}`} className="group flex gap-6 border-t-2 border-line pt-7 transition-colors duration-300 hover:border-gold">
                <span className="font-heading text-4xl font-extrabold text-navy/10 transition-colors duration-300 group-hover:text-gold/40">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="flex items-center gap-2.5 font-heading text-lg font-bold text-navy">
                    <s.icon className="h-5 w-5 text-gold" strokeWidth={1.75} /> {s.title}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-muted">{s.desc}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <FadeUp delay={0.15} className="mt-16">
          <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Countries We Cover</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {COUNTRIES.map((c) => (
              <span key={c.name} data-testid={`visa-country-${c.name.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-3 rounded-full border border-line bg-white py-2 pl-2 pr-5 font-accent text-xs font-semibold uppercase tracking-widest text-navy transition-colors duration-300 hover:border-gold/60">
                <img src={`https://flagcdn.com/w80/${c.code}.png`} alt={`${c.name} flag`} loading="lazy" className="h-8 w-8 rounded-full border border-line object-cover" />
                {c.name}
              </span>
            ))}
          </div>
        </FadeUp>
      </section>

      <section className="relative overflow-hidden bg-navy py-24 text-white" data-testid="visa-cta">
        <div className="noise-overlay absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <FadeUp>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-5xl">
              Your journey abroad starts with a <span className="italic text-gold">simple conversation</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
              With strategic insights, attention to detail and hands-on preparation, we've helped hundreds of students secure their visas. Your success is our mission.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Magnetic>
                <button onClick={() => openConsult()} data-testid="visa-consult-button"
                  className="group flex items-center gap-2 rounded-full bg-gold px-9 py-4 font-accent text-sm font-bold uppercase tracking-widest text-navy transition-colors hover:bg-gold-soft">
                  Book Free Visa Counseling <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </Magnetic>
              <Magnetic>
                <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="visa-whatsapp-button"
                  className="flex items-center gap-2 rounded-full border border-white/25 px-9 py-4 font-accent text-sm font-semibold uppercase tracking-widest text-white transition-colors hover:border-gold hover:text-gold">
                  <MessageCircle className="h-5 w-5" /> WhatsApp Us
                </a>
              </Magnetic>
            </div>
          </FadeUp>
        </div>
      </section>
    </motion.div>
  );
}
